import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce, cloneDeep } from "lodash";

import {
	Button,
	Col,
	Form,
	Input,
	Modal,
	PageHeader,
	Row,
	Select,
	Table,
	Tooltip,
} from "antd";
import moment from "moment-jalaali";

import {
	CheckPermission,
	GetUrlParams,
	ListToTree,
	ObjectToURL,
	PriceFormat,
} from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { MinuteFormat } from "scripts/helpers";
import { Modals, Permissions, Types } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/CourseForm";
import SessionModal from "modals/SessionForm";
import CourseSoundModal from "modals/CourseSoundForm";

const Component = ({
	currency,
	onSetRoute,
	routeName,
	parentName,
	permissions,
}) => {
	const initialState = {
		categories: [],
		data: [],
		files: [],
		videos: [],
		sounds: [],
		images: [],
		sessionImages: [],
	};
	const tableColumns = [
		{
			title: Resources.row,
			dataIndex: "index",
			className: "center-cell",
			width: "60px",
			render: (value, record, index) => index + 1,
		},
		Table.EXPAND_COLUMN,
		{
			title: Resources.title,
			dataIndex: "title",
		},
		{
			title: Resources.show,
			className: "center-cell color-cell",
			dataIndex: "locked",
			width: "80px",
			render: (value) => (
				<span className={value ? "fa fa-lock red" : "fa fa-unlock green"} />
			),
		},
		{
			title: `${Resources.code} ${Resources.course}`,
			className: "center-cell",
			dataIndex: "code",
		},
		{
			title: Resources.category,
			className: "center-cell",
			dataIndex: "category",
			render: (value) => value?.title,
		},
		{
			title: Resources.price,
			className: "center-cell desc-cell",
			dataIndex: "price",
			width: "80px",
			render: (value) => (
				<>
					<span className="name">{PriceFormat(value)}</span>
					<small className="desc">{currency}</small>
				</>
			),
		},
		{
			title: Resources.publish,
			className: "center-cell color-cell",
			dataIndex: "published",
			width: "120px",
			render: (value) => (
				<span className={value ? "blue" : "orange"}>
					{value ? Resources.published : Resources.unpublished}
				</span>
			),
		},
		{
			title: Resources.status,
			className: "center-cell color-cell",
			dataIndex: "status",
			width: "80px",
			render: (value, record) =>
				permissions.status ? (
					<span
						className={`btn ${value ? "green" : "red"}`}
						onClick={() => handleStatus(record)}
					>
						{value ? Resources.active : Resources.inactive}
					</span>
				) : (
					<span className={value ? "green" : "red"}>
						{value ? Resources.active : Resources.inactive}
					</span>
				),
		},
		{
			title: `${Resources.date} ${Resources.create}`,
			dataIndex: "createdAt",
			className: "center-cell desc-cell ltr-cell",
			width: "100px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return (
					<>
						<span className="name">{date}</span>
						<small className="desc">{time}</small>
					</>
				);
			},
		},
		{
			title: Resources.action,
			dataIndex: "id",
			className: "center-cell",
			width: "200px",
			render: (value, record) => (
				<>
					{permissions.sessionCreate && (
						<Tooltip
							title={`${Resources.add} ${Resources.session} ${Resources.video}${Resources.y}${Resources.y}`}
						>
							<Button
								type="link"
								icon={<i className="fa fa-video" />}
								onClick={() => handleSessionForm(record, false, Modals.VIDEO)}
							/>
						</Tooltip>
					)}

					{permissions.sessionCreate && (
						<Tooltip
							title={`${Resources.add} ${Resources.session} ${Resources.sound}${Resources.y}`}
						>
							<Button
								type="link"
								icon={<i className="fa fa-microphone" />}
								onClick={() => handleSessionForm(record, false, Modals.SOUND)}
							/>
						</Tooltip>
					)}
					{permissions.sessionCreate && (
						<Tooltip
							title={`${Resources.add} ${Resources.session} ${Resources.image}${Resources.y}`}
						>
							<Button
								type="link"
								icon={<i className="fa fa-image" />}
								onClick={() => handleSessionForm(record, false, Modals.IMAGE)}
							/>
						</Tooltip>
					)}
					{permissions.sessionCreate && (
						<Tooltip
							title={`${Resources.add} ${Resources.session} ${Resources.text}${Resources.y}`}
						>
							<Button
								type="link"
								icon={<i className="fa fa-sticky-note" />}
								onClick={() => handleSessionForm(record, false, Modals.TEXT)}
							/>
						</Tooltip>
					)}
					{permissions.update && (
						<Tooltip title={Resources.edit}>
							<Button
								type="link"
								icon={<i className="fa fa-pencil-alt" />}
								onClick={() => handleForm(record)}
							/>
						</Tooltip>
					)}
					{permissions.delete && (
						<Tooltip title={Resources.delete}>
							<Button
								type="link"
								icon={<i className="fa fa-trash-alt" />}
								onClick={() => handleDelete(record.code)}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
	const [state, setState] = useState(initialState);
	const {
		categories,
		data,
		fetching,
		loading,
		selected,
		videos,
		sounds,
		images,
		sessionImages,
		files,
	} = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const expandedRowRender = ({ code, id, sessions = [] }) => {
		const columns = [
			{
				title: Resources.row,
				dataIndex: "index",
				className: "center-cell",
				width: "60px",
				render: (value, record, index) => index + 1,
			},
			{
				title: Resources.title,
				dataIndex: "title",
			},
			{
				title: `${Resources.code} ${Resources.session}`,
				className: "center-cell",
				dataIndex: "code",
			},
			{
				title: `${Resources.type} `,
				className: "center-cell",
				dataIndex: "type",
			},
			{
				title: Resources.hasVideo,
				className: "center-cell color-cell",
				dataIndex: "video",
				width: "80px",
				render: (value) => (value?.id) ? <span style={{color: "green"}}>{Resources.yes}</span> :  <span style={{color: "red"}}>{Resources.no}</span>,
			},
			{
				title: Resources.show,
				className: "center-cell color-cell",
				dataIndex: "locked",
				width: "80px",
				render: (value) => (
					<span className={value ? "fa fa-lock red" : "fa fa-unlock green"} />
				),
			},
			{
				title: Resources.duration,
				className: "center-cell desc-cell",
				dataIndex: "duration",
				width: "80px",
				render: (value) =>
					value ? (
						<>
							<span className="name">{MinuteFormat(value)}</span>
							<small className="desc">{Resources.minute}</small>
						</>
					) : (
						"-"
					),
			},

			{
				title: Resources.status,
				className: "center-cell color-cell",
				dataIndex: "status",
				width: "80px",
				render: (value, record) =>
					permissions.sessionStatus ? (
						<span
							className={`btn ${value ? "green" : "red"}`}
							onClick={() =>
								handleSessionStatus({ ...record, courseCode: code })
							}
						>
							{value ? Resources.active : Resources.inactive}
						</span>
					) : (
						<span className={value ? "green" : "red"}>
							{value ? Resources.active : Resources.inactive}
						</span>
					),
			},
			{
				title: `${Resources.date} ${Resources.create}`,
				dataIndex: "createdAt",
				className: "center-cell desc-cell ltr-cell",
				width: "100px",
				render: (value) => {
					const [date, time] = moment(value)
						.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
						.split(" ");

					return (
						<>
							<span className="name">{date}</span>
							<small className="desc">{time}</small>
						</>
					);
				},
			},
			{
				title: Resources.action,
				dataIndex: "id",
				className: "center-cell",
				width: "120px",
				render: (value, record) => (
					<>
						{permissions.sessionUpdate && (
							<Tooltip title={Resources.edit}>
								<Button
									type="link"
									icon={<i className="fa fa-pencil-alt" />}
									onClick={() =>
										handleSessionForm({
											...record,
											courseCode: code,
											courseId: id,
										})
									}
								/>
							</Tooltip>
						)}
						{permissions.sessionDelete && (
							<Tooltip title={Resources.delete}>
								<Button
									type="link"
									icon={<i className="fa fa-trash-alt" />}
									onClick={() =>
										handleSessionDelete({ ...record, courseCode: code })
									}
								/>
							</Tooltip>
						)}
					</>
				),
			},
		];

		return (
			<Table
				columns={columns}
				dataSource={sessions}
				loading={fetching === code}
				pagination={false}
				size="small"
				rowKey="id"
			/>
		);
	};

	const onExpand = (expanded, { code }) => {
		if (expanded) {
			setState((prevState) => ({ ...prevState, fetching: code }));

			API.course
				.details(code)
				.then(({ data: course }) => {
					setState((prevState) => {
						const _data = prevState.data.map((item) =>
							item.code === code ? course : item
						);

						return { ...prevState, data: _data, fetching: null };
					});
				})
				.catch(() => {
					setState((prevState) => ({ ...prevState, fetching: null }));
				});
		}
	};

	const handleSessionForm = (selected, reload, type) => {
		if (reload) {
			onExpand(true, selected);
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(
				`${pathname}${search}#${
					selected?.courseCode ? Modals.EDIT : Modals.CREATE
				}_${Modals.SESSION}${type ? `_${type}` : ""}`
			);
		}
	};

	const handleCourseSoundForm = (selected, reload, type) => {
  // اگر بعد از ثبت مودال خواستیم دیتا رفرش شود
  if (reload) {
    onExpand(true, selected);
    return;
  }

  // ذخیره رکورد انتخاب شده
  setState((prev) => ({
    ...prev,
    selected,
  }));

  // تعیین حالت create یا edit
  const mode = selected?.courseCode ? Modals.EDIT : Modals.CREATE;

  // ساخت hash
  const hash = `${mode}_${Modals.SOUND}${type ? `_${type}` : ""}`;

  // تغییر URL برای باز شدن مودال
  navigate(`${pathname}${search}#${hash}`);
};

	

	const handleSessionStatus = (selected) => {
		Modal.confirm({
			title: Resources.statusConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.session
					.status(selected.code)
					.then(() => {
						onExpand(true, { code: selected.courseCode });
					})
					.finally(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleSessionDelete = (selected) => {
		Modal.confirm({
			title: Resources.deleteConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.session
					.delete(selected.code)
					.then(() => {
						onExpand(true, { code: selected.courseCode });
					})
					.finally(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const fetchData = () => {
		const params = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue(params);

		setState((prevState) => ({ ...prevState, loading: true }));

		API.course
			.list({ ...params, paginate: false })
			.then(({ data }) => {
				setState((prevState) => ({
					...prevState,
					data,
					loading: false,
				}));
			})
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleForm = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(
				`${pathname}${search}#${selected ? Modals.EDIT : Modals.CREATE}`
			);
		}
	};

	const handleStatus = (selected) => {
		Modal.confirm({
			title: Resources.statusConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.course
					.status(selected.code)
					.then(() => {
						setState((prevState) => ({ ...prevState, loading: false }));

						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleDelete = (code) => {
		Modal.confirm({
			title: Resources.deleteConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.course
					.delete(code)
					.then(() => {
						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleFilter = debounce(() => {
		form.validateFields().then((values) => {
			const search = ObjectToURL({ page: 1, ...values });

			navigate(`${pathname}?${search}`);
		});
	}, 1000);

	const componentDidUpdate = () => {
		fetchData();
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });

		Promise.all([
			API.category.list({ paginate: false }),
			API.videos.list({ paginate: false, section: Types.upload.course }),
			API.gallery.list({ paginate: false, section: "course" }),
			API.gallery.list({ paginate: false, section: "session" }),
			API.files.list({ paginate: false }),
			API.sounds.list({ paginate: false, section: "course" }),
			
		])
			.then(
				([
					{ data: categories },
					{ data: videos },
					{ data: images },
					{ data: sessionImages },
					{ data: files },
					{ data: sounds },
				]) => {
					setState((prevState) => ({
						...prevState,
						categories: ListToTree(cloneDeep(categories), true),
						videos: videos.map(({ id, title }) => ({
							value: id,
							label: title,
						})),
						images: images.map(({ id, title, file }) => ({
							value: id,
							file,
							label: title,
						})),
						sessionImages: sessionImages.map(({ id, title, file }) => ({
							value: id,
							file,
							label: title,
						})),
						files: files.map(({ id, title }) => ({
							value: id,
							label: title,
						})),
						sounds: sounds.map(({ id, title }) => ({
							value: id,
							label: title,
						})),
					}));
				}
			)
			.catch(() => {});
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader
				title={`${Resources.course}${Resources.ha}`}
				extra={
					permissions.create && (
						<Button
							type="primary"
							onClick={() => handleForm()}
							disabled={loading}
						>
							{Resources.add}
						</Button>
					)
				}
			/>

			<Form
				layout="vertical"
				form={form}
			>
				<Row gutter={24}>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="search">
							<Input
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								placeholder={Resources.search}
								allowClear
								autoFocus
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="category">
							<Select
								onChange={handleFilter}
								options={categories}
								placeholder={Resources.category}
								allowClear
							/>
						</Form.Item>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="status">
							<Select
								onChange={handleFilter}
								options={[
									{ value: "true", label: Resources.active },
									{ value: "false", label: Resources.inactive },
								]}
								placeholder={Resources.status}
								allowClear
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				pagination={false}
				scroll={{ x: 930 }}
				expandable={{
					expandedRowRender,
					onExpand,
				}}
				rowKey="id"
			/>

			<FormModal
				data={selected}
				onUpdate={handleForm}
				options={{ categories, images }}
			/>

			<SessionModal
				data={selected}
				onUpdate={handleSessionForm}
				options={{ videos, sessionImages, files, sounds }}
			/>

			<CourseSoundModal
				data={selected}
				onUpdate={handleCourseSoundForm}
				options={{ sounds, files }}
				/>

			
		</>
	);
};

export default connect(
	(state) => {
		return {
			currency: state.settings.currency,
			permissions: {
				create: CheckPermission(
					state.user.permissions,
					Permissions.COURSE_CREATE
				),
				delete: CheckPermission(
					state.user.permissions,
					Permissions.COURSE_DELETE
				),
				update: CheckPermission(
					state.user.permissions,
					Permissions.COURSE_UPDATE
				),
				status: CheckPermission(
					state.user.permissions,
					Permissions.COURSE_STATUS
				),
				sessionCreate: CheckPermission(
					state.user.permissions,
					Permissions.SESSION_CREATE
				),
				sessionDelete: CheckPermission(
					state.user.permissions,
					Permissions.SESSION_DELETE
				),
				sessionUpdate: CheckPermission(
					state.user.permissions,
					Permissions.SESSION_UPDATE
				),
				sessionStatus: CheckPermission(
					state.user.permissions,
					Permissions.SESSION_STATUS
				),
			},
		};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
